import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import EditEmailModal from '@/shared/components/channels/EditEmailModal.vue';
import { DEFERRED_SUBMIT } from '@/shared/components/channels/submitDeferral';
import { useAppToast } from '@/shared/composables/useAppToast';
import { resolveApiErrorMessage } from '@/infrastructure/services/ApiErrorService';
import { logger } from '@/infrastructure/services/LoggerService';
import { SHARED_EMAIL_DISABLED_ERROR_CODE, useSharedEmailSetting } from '@/modules/workspace/public';
import DesignatePrimaryContactModal from './DesignatePrimaryContactModal.vue';
import ConfirmLastSharedOwnerModal from './ConfirmLastSharedOwnerModal.vue';
import { checkContactDuplicates, getContactSharedEmailGroup, updateContactEmail, } from '../../infrastructure/adapters/contactService';
import { contactDisplayName } from '../../domain/contactDisplayName';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ContactEditEmailModal',
    props: /*@__PURE__*/ _mergeModels({
        contactId: {},
        channelId: {},
        initialValue: {},
        initialIsPrimary: { type: Boolean },
        isShared: { type: Boolean, default: false },
        sharedOwnerRole: { default: null }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["updated", "delete"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /**
         * Contact binding of the reusable {@link EditEmailModal} (OP-5609 / US-13), extended with the
         * shared-email ownership transfer of OP-5804 (US-04c).
         *
         * On save, when the edited channel is the PRIMARY of a shared email group and the value
         * changed (the contact leaves the group), the flow forks:
         * - 2+ members would remain → the save is deferred ({@link DEFERRED_SUBMIT}) and the
         *   {@link DesignatePrimaryContactModal} asks which member becomes the new primary; the
         *   confirmation re-issues ONE `PUT` carrying both the new value and `newPrimaryChannelId`
         *   (single OPHUB transaction).
         * - 1 member would remain → plain `PUT`; OPHUB auto-dissolves the 2-member group.
         *
         * Cas 2 (US-04c): when the NEW value already belongs to another contact and the workspace allows
         * shared emails, {@link EditEmailModal} surfaces the share opt-in; the resulting `isShared` rides the same `PUT`. Combined with the
         * transfer above, a single `PUT` then carries `isShared` (join the new value's group) AND
         * `newPrimaryChannelId` (leave the old group), committed atomically by OPHUB.
         */
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const { sharedEmailsEnabled, sharedEmailSettingPending, loadSharedEmailSetting, onEmailWriteRejected } = useSharedEmailSetting();
        watch(open, (isOpen) => { if (isOpen)
            void loadSharedEmailSetting(); }, { immediate: true });
        const emit = __emit;
        const { t } = useI18n();
        const { toastSuccess } = useAppToast();
        // errorCode → i18n key (R70). The 255-char cap is guarded client-side, so this only fires as
        // defense-in-depth; the generic fallback covers everything else. Reuses the add-email key.
        // `TransferRequired` only fires if the designation gate raced stale group data; `AlreadyExists`
        // only if detection missed the collision (Save stays disabled on a detected one until shared).
        // `SharedEmail.Disabled` is reachable: the setting can be turned off after the modal opened.
        const ERROR_OVERRIDES = {
            CHANNEL_VALUE_TOO_LONG: 'contact.detail.addEmail.validation.emailTooLong',
            'CommunicationChannel.SharedEmail.TransferRequired': 'contact.detail.editEmail.errors.transferRequired',
            'CommunicationChannel.Email.AlreadyExists': 'contact.detail.editEmail.errors.emailAlreadyExists',
            [SHARED_EMAIL_DISABLED_ERROR_CODE]: 'contact.detail.addEmail.errors.sharedEmailsDisabled',
            // `CommunicationChannel.Email.Invalid` (blocked TLDs, OP-7457) is mapped by the shared
            // modal's built-in defaults (channelErrorOverrides.ts).
        };
        /**
         * Real-time duplicate predicate for the edit modal (US-04c Cas 2). Returns the display names of
         * OTHER contacts already using the typed value (the current contact is excluded — editing among
         * one's own channels is never a collision). Drives the share opt-in box when sharing is enabled.
         */
        async function checkAlreadyUsed(email) {
            const matches = await checkContactDuplicates({ email });
            return matches
                .filter((m) => m.emailExactMatch && m.id !== props.contactId)
                .map((m) => m.fullName);
        }
        // Designation-modal failures (R70): the selected target became invalid between the group
        // fetch and the confirm (member left / channel deleted concurrently).
        const DESIGNATION_ERROR_OVERRIDES = {
            'CommunicationChannel.SharedEmail.InvalidTransferTarget': 'contact.detail.designatePrimary.errors.invalidTarget',
            'CommunicationChannel.SharedEmail.TransferTargetMustDiffer': 'contact.detail.designatePrimary.errors.invalidTarget',
        };
        const showDesignateModal = ref(false);
        const designationCandidates = ref([]);
        const designating = ref(false);
        const designationError = ref(null);
        const pendingPayload = ref(null);
        // OP-5913 — single-survivor confirmation (the primary leaves, exactly one member remains, OPHUB
        // auto-dissolves the group). The write is unchanged (no newPrimaryChannelId); this only gates it.
        const showLastOwnerConfirm = ref(false);
        const lastOwnerSurvivor = ref(null);
        const lastOwnerConfirming = ref(false);
        const lastOwnerError = ref(null);
        const lastOwnerName = computed(() => contactDisplayName(lastOwnerSurvivor.value?.contactName, t));
        async function submitEmail(payload) {
            const leavesSharedGroup = props.isShared &&
                props.sharedOwnerRole === 'Principal' &&
                // `payload.value` is already trimmed by EditEmailModal; trim initialValue too so the
                // "value changed" decision matches EditEmailModal.valueChanged exactly (no whitespace-only diff).
                payload.value !== props.initialValue.trim();
            if (leavesSharedGroup) {
                const members = await getContactSharedEmailGroup(props.contactId, props.channelId);
                const remaining = members.filter((m) => m.channelId !== props.channelId);
                // 2+ remaining members: OPHUB requires a transfer target on the same call — defer the
                // save to the designation modal. The Cas 2 `isShared` opt-in (join the new value's group)
                // rides the deferred PUT too — see confirmDesignation.
                if (remaining.length >= 2) {
                    designationCandidates.value = remaining;
                    pendingPayload.value = payload;
                    designationError.value = null;
                    showDesignateModal.value = true;
                    return DEFERRED_SUBMIT;
                }
                // OP-5913 — exactly one member remains: OPHUB auto-dissolves the group (no transfer target),
                // so the PUT is unchanged. Confirm first that the lone survivor becomes the primary owner.
                if (remaining.length === 1) {
                    lastOwnerSurvivor.value = remaining[0];
                    pendingPayload.value = payload;
                    lastOwnerError.value = null;
                    showLastOwnerConfirm.value = true;
                    return DEFERRED_SUBMIT;
                }
            }
            return updateContactEmail(props.contactId, props.channelId, {
                value: payload.value,
                isPrimary: payload.isPrimary,
                isShared: payload.isShared,
            }).catch((error) => {
                onEmailWriteRejected(error);
                throw error;
            });
        }
        // OP-5913 — single-survivor confirm: run the (unchanged) PUT, then dismiss both modals. OPHUB
        // dissolves the group; the survivor becomes the sole/primary owner (no newPrimaryChannelId sent).
        async function confirmLastOwner() {
            if (!pendingPayload.value)
                return;
            lastOwnerConfirming.value = true;
            lastOwnerError.value = null;
            try {
                await updateContactEmail(props.contactId, props.channelId, {
                    value: pendingPayload.value.value,
                    isPrimary: pendingPayload.value.isPrimary,
                    isShared: pendingPayload.value.isShared,
                });
                const survivorName = lastOwnerName.value;
                showLastOwnerConfirm.value = false;
                open.value = false;
                pendingPayload.value = null;
                lastOwnerSurvivor.value = null;
                emit('updated');
                toastSuccess('contact.detail.editEmail.sharedTransferSuccess', { name: survivorName });
            }
            catch (error) {
                lastOwnerError.value = resolveApiErrorMessage(error, DESIGNATION_ERROR_OVERRIDES, 'contact.detail.designatePrimary.errors.serverError');
                logger.error('Failed to dissolve shared email group on edit', error);
            }
            finally {
                lastOwnerConfirming.value = false;
            }
        }
        // Abandon the single-survivor confirmation: return to the still-mounted edit modal, apply nothing.
        function cancelLastOwner() {
            showLastOwnerConfirm.value = false;
            pendingPayload.value = null;
            lastOwnerSurvivor.value = null;
            lastOwnerError.value = null;
        }
        async function confirmDesignation(newPrimaryChannelId) {
            if (!pendingPayload.value)
                return;
            designating.value = true;
            designationError.value = null;
            try {
                await updateContactEmail(props.contactId, props.channelId, {
                    value: pendingPayload.value.value,
                    isPrimary: pendingPayload.value.isPrimary,
                    isShared: pendingPayload.value.isShared,
                    newPrimaryChannelId,
                });
                const promoted = designationCandidates.value.find((m) => m.channelId === newPrimaryChannelId);
                const promotedName = contactDisplayName(promoted?.contactName, t);
                showDesignateModal.value = false;
                open.value = false;
                pendingPayload.value = null;
                emit('updated');
                toastSuccess('contact.detail.editEmail.sharedTransferSuccess', { name: promotedName });
            }
            catch (error) {
                designationError.value = resolveApiErrorMessage(error, DESIGNATION_ERROR_OVERRIDES, 'contact.detail.designatePrimary.errors.serverError');
                logger.error('Failed to transfer shared email ownership', error);
            }
            finally {
                designating.value = false;
            }
        }
        function closeDesignation() {
            showDesignateModal.value = false;
            pendingPayload.value = null;
            designationError.value = null;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(EditEmailModal, {
                    modelValue: open.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((open).value = $event)),
                    "initial-value": __props.initialValue,
                    "initial-is-primary": __props.initialIsPrimary,
                    submit: submitEmail,
                    "error-overrides": ERROR_OVERRIDES,
                    "check-already-used": checkAlreadyUsed,
                    "shared-emails-enabled": _unref(sharedEmailsEnabled),
                    "shared-email-setting-pending": _unref(sharedEmailSettingPending),
                    "i18n-prefix": "contact.detail.editEmail",
                    "field-i18n-prefix": "contact.detail.addEmail",
                    onUpdated: _cache[1] || (_cache[1] = ($event) => (emit('updated'))),
                    onDelete: _cache[2] || (_cache[2] = ($event) => (emit('delete')))
                }, null, 8 /* PROPS */, ["modelValue", "initial-value", "initial-is-primary", "shared-emails-enabled", "shared-email-setting-pending"]),
                _createVNode(DesignatePrimaryContactModal, {
                    modelValue: showDesignateModal.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showDesignateModal).value = $event)),
                    members: designationCandidates.value,
                    submitting: designating.value,
                    "server-error": designationError.value,
                    onBack: closeDesignation,
                    onConfirm: confirmDesignation
                }, null, 8 /* PROPS */, ["modelValue", "members", "submitting", "server-error"]),
                _createVNode(ConfirmLastSharedOwnerModal, {
                    modelValue: showLastOwnerConfirm.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((showLastOwnerConfirm).value = $event)),
                    mode: "edit",
                    name: lastOwnerName.value,
                    submitting: lastOwnerConfirming.value,
                    "server-error": lastOwnerError.value,
                    onConfirm: confirmLastOwner,
                    onCancel: cancelLastOwner
                }, null, 8 /* PROPS */, ["modelValue", "name", "submitting", "server-error"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
